import { useCallback, useRef, useState } from 'react';
import { useIntl, useSelector, shallowEqual } from 'umi';
import {
  downloadCosResource,
  type TCosExportFormat,
} from '@home/utils/htmlExport';
import { genUserWatermarkDataUrl } from '@home/utils/watermark';
import {
  IAnomalyReportItem,
  pickAnomalyReport,
  resolveAnomalyReportLocale,
  ANOMALY_REPORT_COS_TYPE,
} from '../utils';
import type { IAnomalyReportListState } from './useAnomalyReportList';

interface IHomeUserInfo {
  openid?: string;
  userid?: string;
  code?: string | number;
}

interface IHomeRootState {
  home?: {
    userInfo?: IHomeUserInfo | null;
  };
}

/** 异动日报支持的下载格式（zip 暂未对业务开放） */
export type TDownloadFormat = Extract<TCosExportFormat, 'html' | 'pdf'>;

const FALLBACK_REPORT_STATE: IAnomalyReportListState = {
  loading: false,
  data: null,
  errorMsg: '',
};

export interface IUseReportDownloadOptions {
  reportState: IAnomalyReportListState;
  /** 下载文件名（英文），默认 'AI Daily Report' */
  downloadFileNameEn?: string;
  /** 下载文件名（中文），默认 'AI 异动日报' */
  downloadFileNameCn?: string;
}

export interface IUseReportDownloadResult {
  /** 当前可用的报告 URL（已签名），无报告时为空字符串 */
  reportUrl: string;
  /** 当前报告的目标日期 */
  targetDate: string;
  /** 是否有可下载的报告 */
  canDownload: boolean;
  /** 下载中状态 */
  downloading: boolean;
  /** 下载报告，返回 true 表示成功，false 表示失败 */
  downloadReport: (format?: TDownloadFormat) => Promise<boolean>;
}

/**
 * 异动日报下载 Hook
 *
 * 业务层只负责：选报告、拼文件名、用户水印、loading。
 * 导出实现见 `@home/utils/htmlExport`；水印图案见 `@home/utils/watermark`（与 Mask 同源）。
 */
export const useReportDownload = ({
  reportState,
  downloadFileNameEn = 'AI Daily Report',
  downloadFileNameCn = 'AI 异动日报',
}: IUseReportDownloadOptions): IUseReportDownloadResult => {
  const { locale } = useIntl();
  const [downloading, setDownloading] = useState<boolean>(false);
  // 用 ref 做防重入 guard，避免将 downloading state 放入 useCallback 依赖导致函数频繁重建
  const downloadingRef = useRef(false);

  const userInfo = useSelector(
    (state: IHomeRootState) => state?.home?.userInfo,
    shallowEqual,
  );

  const safeReportState: IAnomalyReportListState = reportState || FALLBACK_REPORT_STATE;

  const list = safeReportState.data as IAnomalyReportItem[] | undefined;
  const reportInfo = pickAnomalyReport(
    list,
    resolveAnomalyReportLocale(locale),
  );

  const reportUrl = reportInfo?.url || '';
  const targetDate = reportInfo?.targetDate || '';
  const canDownload = !!reportUrl && !safeReportState.loading;

  const buildFileName = useCallback(
    (format: TDownloadFormat): string => {
      const baseName =
        locale === 'zh-CN' ? downloadFileNameCn : downloadFileNameEn;
      const prefix = targetDate ? `${baseName}-${targetDate}` : baseName;
      return `${prefix}.${format}`;
    },
    [targetDate, locale, downloadFileNameEn, downloadFileNameCn],
  );

  const downloadReport = useCallback(
    async (format: TDownloadFormat = 'html'): Promise<boolean> => {
      if (!reportUrl || downloadingRef.current) {
        return false;
      }

      downloadingRef.current = true;
      setDownloading(true);
      try {
        const watermarkDataUrl = genUserWatermarkDataUrl(userInfo);
        await downloadCosResource({
          url: reportUrl,
          cosType: ANOMALY_REPORT_COS_TYPE,
          format,
          fileName: buildFileName(format),
          watermark: watermarkDataUrl
            ? { dataUrl: watermarkDataUrl }
            : undefined,
        });
        return true;
      } catch (error) {
        console.error('[AbnormalReport] download failed:', error);
        return false;
      } finally {
        downloadingRef.current = false;
        setDownloading(false);
      }
    },
    [reportUrl, buildFileName, userInfo],
  );

  return {
    reportUrl,
    targetDate,
    canDownload,
    downloading,
    downloadReport,
  };
};
